import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import ExibirPerguntasFrequentesPorSlug from "../components/ExibirPerguntasFrequentesPorSlug";
import CampoPergunta from "../components/CampoPergunta";
import { montarPerguntaComPrint } from "../lib/perguntaComPrint";
import { useSugestoes } from "../hooks/Sugestoes";
import { usePergunta } from "../contexts/PerguntaContext";
import HistoricoConversas from "../components/HistoricoConversa";
import ExibirRespostaGPT from "../components/ExibirRespostaGPT";
import { useSalvarConversaNoStorage } from "../hooks/SalvarConversaNoStorage";
import ObterHistoricoConversa from "../hooks/ObterHistoricoConversa";
import { useTranslation } from "react-i18next";
import { EmpreenderApps } from "../files/AppsArray";
import ExibirContatosWhatsApp from "../components/ExibirContatosWhatsApp";
import i18n from "../i18n";
import SEO from "../components/SEO";
import SuporteIcon from "../empreenderUI/ui/appsIcons/SuporteIcon";
import { useFalarComAtendente } from "../hooks/FalarComAtendente";
import { rota } from "../lib/rotas";
import { registrarEvento } from "../lib/metricas";
const { v4: uuidv4 } = require("uuid");

interface TelaInicialProps {
  conversaAtual: string;
  appSlugAmigavel: string;
}

const TelaChat = ({ conversaAtual, appSlugAmigavel }: TelaInicialProps) => {
  const {
    pergunta,
    setPergunta,
    conversaAtual: conversaDoContexto,
    setConversaAtual,
    erroPergunta,
    setErroPergunta,
    contextoPendente,
    setContextoPendente,
  } = usePergunta();
  const [inputValue, setInputValue] = useState("");
  const [, setLocation] = useLocation();
  const containerChatRef = useRef<HTMLDivElement | null>(null);
  const idiomaAtual = i18n.language;

  const dadosApp = EmpreenderApps.find(
    (app) => app.slugamigavel === appSlugAmigavel
  );

  const appSlug =
    idiomaAtual === "pt" ? dadosApp?.slug || "" : dadosApp?.sluges || "";
  const nomeApp =
    (idiomaAtual === "es" ? dadosApp?.namees : dadosApp?.name) || "";

  // A URL manda. Depois de recarregar a página (ou abrir um link de conversa), o contexto
  // nascia com outro id: a pergunta ia para a conversa da URL e a resposta para outra,
  // e nada aparecia na tela.
  useEffect(() => {
    if (conversaAtual && conversaAtual !== conversaDoContexto) {
      setConversaAtual(conversaAtual);
    }
  }, [conversaAtual, conversaDoContexto]);

  const historico = ObterHistoricoConversa(appSlug, conversaAtual);

  const { salvarConversa } = useSalvarConversaNoStorage();
  const { acesso, chamarAtendente } = useFalarComAtendente(appSlug);

  // Atalho discreto para o WhatsApp: só para quem já marcou uma resposta da IA como "não
  // resolveu" nesta conversa (antes disso o atendente fica no "O que faltou?"). Some enquanto
  // o cartão de contatos já está na tela.
  const naoResolveu = historico.some((m) => m.tipo === "assistente" && m.feedback === "nao");
  const contatosNaTela = historico[historico.length - 1]?.texto === "pediu_ajuda";
  const mostrarAtalhoAtendente =
    naoResolveu && !contatosNaTela && !pergunta && acesso === "liberado";
  const { t } = useTranslation();
  const { paraAutocompletar } = useSugestoes(
    appSlug,
    idiomaAtual === "es" ? "es" : "pt"
  );

  const enviarPergunta = (texto: string, textoImagem?: string) => {
    const { exibicao, api, extras } = montarPerguntaComPrint(texto, textoImagem, t);
    if (!exibicao || pergunta) return;

    salvarConversa(appSlug, conversaAtual, { usuario: exibicao }, undefined, undefined, undefined, extras);
    // Depois de "não resolveu", os detalhes vão junto com a pergunta original: a SAK
    // recebe só a mensagem atual e responderia sem contexto.
    setPergunta(
      contextoPendente ? `${contextoPendente}\n\n${t("fb_api_detalhes")}: ${api}` : api
    );
    setContextoPendente(null);
    setErroPergunta(null);
    setInputValue("");
  };

  const tentarDeNovo = () => {
    if (!erroPergunta) return;
    setErroPergunta(null);
    setPergunta(erroPergunta);
  };

  const novaConversa = () => {
    setConversaAtual(uuidv4());
    setContextoPendente(null);
    setErroPergunta(null);
    setLocation(rota(`/${appSlugAmigavel}/${idiomaAtual}`));
  };

  const scrollToBottomCustom = (element: HTMLElement, duration: number) => {
    const start = element.scrollTop;
    const end = element.scrollHeight - element.clientHeight;
    const change = end - start;
    const startTime = performance.now();

    const easeInOutQuad = (t: number) =>
      t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      element.scrollTop = start + change * easeInOutQuad(progress);

      if (progress < 1) requestAnimationFrame(animateScroll);
    };

    requestAnimationFrame(animateScroll);
  };

  const scrollToBottom = () => {
    if (containerChatRef.current) {
      scrollToBottomCustom(containerChatRef.current, 1000);
    }
  };

  // Rola até o fim só quando a conversa avança (mensagem nova, carregando, erro). Antes um
  // MutationObserver rolava a cada mudança no DOM: clicar num vídeo antigo trocava a capa
  // pelo player e a tela descia até a última resposta.
  useEffect(scrollToBottom, [pergunta, conversaAtual, historico.length, erroPergunta]);

  return (
    <div className="flex flex-col tela-chat" style={{ height: "calc(100% - 60px)" }}>
      <SEO
        titleKey="seo_chat_title"
        descriptionKey="seo_chat_description"
        args={{ appName: dadosApp?.name || t("suporte_empreender") }}
      />

      <div className="cabecalho-chat">
        <div className="cabecalho-chat-app">
          <span className="cabecalho-chat-icone">{dadosApp?.icon || <SuporteIcon />}</span>
          <span>{t("suporte_do_app", { appName: nomeApp })}</span>
          <span className="cabecalho-chat-idioma">
            {idiomaAtual === "es" ? "Español" : "Português"}
          </span>
        </div>
        <button className="botao-secundario" onClick={novaConversa}>
          {t("nova_conversa")}
        </button>
      </div>

      {/* A conversa ocupa todo o espaço que sobra; a área de baixo tem só a altura do campo
          (antes era 80%/20% fixos, e no celular os 20% viravam um bloco branco vazio). */}
      <div className="flex-grow overflow-y-auto min-h-0" ref={containerChatRef}>
        <div id="container-chat" className="flex justify-center">
          <div id="box-conversas" className="pt-3">
            <HistoricoConversas
              slugAtual={appSlug}
              conversaAtual={conversaAtual}
            />
            {pergunta && pergunta !== "pediu_ajuda" && (
              <ExibirRespostaGPT slugAtual={appSlug} />
            )}
            {pergunta === "pediu_ajuda" && (
              <ExibirContatosWhatsApp slugAtual={appSlug} />
            )}
            {erroPergunta && !pergunta && (
              <div className="cartao-suporte cartao-erro">
                <p className="cartao-suporte-titulo">{t("erro_titulo")}</p>
                <p className="cartao-suporte-texto">{t("erro_texto")}</p>
                <button className="botao-primario" onClick={tentarDeNovo}>
                  {t("tentar_de_novo")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-none box-categorias-input-conversa">
        <div id="container-input" className="flex justify-center w-full">
          <div id="box-input-conversa">
            <div
              id="box-input-duvida-conversa"
              className="flex items-center justify-between w-full"
            >
              <CampoPergunta
                id="input-duvida-conversa"
                valor={inputValue}
                aoMudar={setInputValue}
                aoEnviar={enviarPergunta}
                placeholder={
                  contextoPendente ? t("placeholder_detalhes") : t("diga_sua_duvida")
                }
                desabilitado={pergunta !== ""}
                // Depois de "não resolveu", a pessoa descreve o problema: sugerir perguntas atrapalharia.
                opcoes={contextoPendente ? [] : paraAutocompletar}
                idioma={idiomaAtual === "es" ? "es" : "pt"}
                abrirPara="cima"
              />
            </div>
            {mostrarAtalhoAtendente && (
              <p className="atalho-atendente">
                {t("atalho_atendente_texto")}{" "}
                <button
                  onClick={() => {
                    registrarEvento({ tipo: "atendente", conversa: conversaAtual, app: appSlug, detalhe: "rodape" });
                    chamarAtendente();
                  }}
                >
                  {t("atalho_atendente_botao")}
                </button>
              </p>
            )}
            <ExibirPerguntasFrequentesPorSlug
              appSlug={appSlug}
              aoEscolher={enviarPergunta}
              desabilitado={pergunta !== ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelaChat;
